import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermissions, useTranslate } from 'react-admin';
import { Box, Typography, Paper } from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid';

// Services
import { getStocks } from '../services/stocksService';

// Components
import DataGridTable from '../components/table/DataGridTable';
import EmptyList from '../components/misc/EmptyList';
import RefreshButton from '../components/misc/RefreshButton';

// Definitions
import { STOCKS_TABLE } from '../definitions';

// Utils
import { 
  checkStocksPermissions, 
  getStocksPageTitle, 
  getManagerColumns 
} from '../utils/stocksUtils';

// Constants
const LAST_X_ROWS = 100;

const StocksPage = () => {
  const translate = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState(STOCKS_TABLE.columns);
  const { permissions } = usePermissions();

  // Get query params
  const eventID = searchParams.get('eventID');

  // Check permissions
  const { isManager, canView } = checkStocksPermissions(permissions);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const rows = await getStocks({ 
        eventID, 
        limitCount: LAST_X_ROWS 
      });
      
      // Filter columns for managers
      if (isManager) {
        setColumns(getManagerColumns(STOCKS_TABLE.columns));
      }
      
      setData(rows);
    } catch (error) {
      console.error('Error fetching stocks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (permissions) {
      // Redirect managers without eventID to events page
      if (isManager && !eventID) {
        window.location.href = '/#/events-v2';
        return;
      }

      fetchData();
    }
  }, [searchParams, permissions, isManager, eventID]);

  const CustomTableHeader = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <RefreshButton refreshData={fetchData} />
    </GridToolbarContainer>
  );

  // Get page title and description
  const { title, description } = getStocksPageTitle(eventID, translate);

  // Loading states
  if (!permissions) {
    return <EmptyList mode="loading" />;
  }

  if (isLoading) {
    return <EmptyList mode="loading" />;
  }

  // Permission checks
  if (!canView) {
    return (
      <EmptyList
        customMessage={translate('stocks.no_permission', {
          _: "You don't have the right permission set to view this page. Please contact GişeKıbrıs if you believe this is an error.",
        })}
        noCreate={true}
      />
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Header Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Paper>

      {/* Data Table Section */}
      <Paper sx={{ flex: 1, p: 2 }}>
        <DataGridTable
          config={STOCKS_TABLE}
          rows={data}
          columns={columns}
          loading={isLoading}
          sx={{
            '.MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
          }}
          slots={{ toolbar: CustomTableHeader }}
        />
      </Paper>
    </Box>
  );
};

export default StocksPage;
